<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal :visible="modal.show" :data="model" :title="computedTitle" @show="onShow"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" :content-in-form="false" size="md" scrollable>
      <div class="row">
        <div class="col-md-6">
          <customer-select-input-widget attribute="related_customer_uuid" :model="model" :extra-filters="extraFilters"
                                        @select="onCustomerSelect" @unselect="onCustomerUnSelect"/>
        </div>
        <div class="col-md-6" v-if="relationType === 'intermediary'">
          <percentage-input-widget :model="model" attribute="intermediary_commission_rate"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="reference_number"/>
        </div>
        <div class="col-md-6" v-if="relationType === 'intermediary'">
          <input-widget :model="model"
                        attribute="agreement_type"
                        value-field="value"
                        display-field="${item.label}"
                        type="select2"
                        :items="intermediaryAgreementTypes"/>
        </div>

      </div>
      <div class="row">
        <div :class="relationType === 'intermediary' ? 'col-md-6' : 'col-md-4'">
          <input-widget :model="model" attribute="confirmations" type="checkbox" is-switch/>
        </div>
        <div :class="relationType === 'intermediary' ? 'col-md-6' : 'col-md-4'">
          <input-widget :model="model" attribute="suggestions" type="checkbox" is-switch/>
        </div>
        <div :class="relationType === 'intermediary' ? 'col-md-6' : 'col-md-4'">
          <input-widget :model="model" attribute="settlements" type="checkbox" is-switch/>
        </div>
        <div :class="relationType === 'intermediary' ? 'col-md-6' : 'col-md-4'">
          <input-widget :model="model" attribute="cc" type="checkbox" is-switch/>
        </div>
        <div class="col-md-6" v-if="relationType === 'intermediary'">
          <input-widget :model="model" attribute="hide_from_communication" type="checkbox"
                        :disabled="!$hasPermission('change_invoice') && !$hasPermission('change_settlement')"
                        is-switch/>
        </div>
        <div class="col-md-6" v-if="relationType === 'intermediary'">
          <input-widget :model="model" attribute="including_vat" type="checkbox"
                        :disabled="disableIncludingVat"
                        is-switch/>
        </div>

        <div class="col-md-12" v-if="relationType === 'intermediary'">
          <input-widget :model="model" attribute="note" type="textarea"/>
        </div>

      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../../../core/components/artisio-modal/ArtisioModal";
  import InputWidget from "../../../../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import CustomerSelectInputWidget from "../../../../../core/widgets/CustomerSelectInputWidget";
  import RelationModel from "@/modules/customer/models/Relation.model";
  import PercentageInputWidget from "@/core/widgets/PercentageInputWidget";
  import {SOURCE_CUSTOMER_DASHBOARD, SOURCE_RECEIPT_DASHBOARD} from "@/modules/constants";
  import RelationsService from "@/modules/relations/RelationsService";

  const {mapState, mapActions} = createNamespacedHelpers('customer/customerView');
  const {mapActions: receiptMapActions} = createNamespacedHelpers('receipts');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "RelationsModal",
    components: {PercentageInputWidget, CustomerSelectInputWidget, InputWidget, ArtisioModal},
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new RelationModel(),
        disableIncludingVat: false
      }
    },
    computed: {
      ...mapState({
        customer: state => state.customer,
        modal: state => state.relationModal,
        editObject: state => state.relationModal.object,
        relationType: state => state.relationModal.relationType,
        title: state => state.relationModal.title,
      }),
      ...globalMapState({
        intermediaryAgreementTypes: state => state.intermediaryAgreementTypes,
      }),
      extraFilters() {
        if (this.relationType === 'intermediary') {
          return {is_intermediary: 'true'}
        } else if (this.relationType === 'notary') {
          return {type: 'notary'}
        } else if (this.relationType === 'contact_person' || this.relationType === 'person_of_interest') {
          return {is_intermediary: 'false', is_notary: 'false'}
        }
        return {}
      },
      computedTitle() {
        return this.title ? this.title : this.$i18n.t("Customer Relation")
      }
    },
    watch: {
      editObject() {
        if (this.editObject) {
          this.model = new RelationModel(this.editObject);
        }
      }
    },
    methods: {
      ...receiptMapActions({
        getReceiptDashboardRelations: 'getDashboardRelations',
      }),
      ...mapActions(['hideRelationModal', 'getDashboardRelations']),
      onShow() {
        this.showConfirmation = true
        if (this.model.related_customer && this.model.related_customer.intermediary_type === 'intermediary_ib47') {
          this.disableIncludingVat = true
          this.model.including_vat = true
        }
      },
      onHidden() {
        this.model = new RelationModel();
        this.hideRelationModal();
        this.disableIncludingVat = false
      },
      async onSubmit() {
        this.loading = true;
        let response;

        if (this.model.uuid) {
          response = await RelationsService.updateRelation(this.model.uuid, this.model)
        } else {
          response = await RelationsService.createRelation(this.model)
        }
        this.loading = false;

        if (response.success) {
          this.$toast('success', this.$i18n.t("Relation has been successfully saved"))
          this.showConfirmation = false

          if (this.modal.source === SOURCE_CUSTOMER_DASHBOARD) {
            this.getDashboardRelations()
          } else if (this.modal.source === SOURCE_RECEIPT_DASHBOARD) {
            this.getReceiptDashboardRelations()
          }

          this.hideRelationModal();
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            } else if (response.toastError) {
              this.$errorDialog(response.toastError);
            }
          }
        }
      },
      onCustomerSelect(customer) {
        if (this.relationType !== 'intermediary') {
          return
        }
        if (customer.intermediary_type === 'intermediary_ib47') {
          this.disableIncludingVat = true
          this.model.including_vat = true
        } else {
          this.disableIncludingVat = false
          this.model.including_vat = true
        }

      },
      onCustomerUnSelect() {
        this.disableIncludingVat = false
        this.model.including_vat = false
      }
    }
  }
</script>

<style scoped>

</style>
